import '../../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Plugins/RichTextToolbarPlugin/RichTextToolbarPlugin.scss'
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND } from '@lexical/list'
import { $createHeadingNode } from '@lexical/rich-text'
import { $setBlocksType } from '@lexical/selection'
import classNames from 'classnames'
import { $createParagraphNode, $getSelection, $isRangeSelection, $setSelection, type BaseSelection } from 'lexical'
import { type FC, useRef, useState } from 'react'
import { Dropdown } from 'react-bootstrap'
import { useRichTextToolbar } from '../../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Plugins/RichTextToolbarPlugin/hooks'
import RichTextLinkEditor from '../../../../apps/professional-app/fa/MeetingSummary/MeetingResults/Lexical/Plugins/RichTextToolbarPlugin/RichTextLinkEditor'
import { Button } from '../../../../components/Button/Button'
import { SelectDropdown, type SelectOption } from '../../../../components/SelectDropdown/SelectDropdown'
import styles from './EmailEditor.module.scss'
import { type EmailVariable, type SlotFormat } from './emailTemplateTypes'
import { InsertVariableMenu } from './InsertVariableMenu'
import { $createVariableNode } from './nodes/VariableNode'

export interface ActiveSlot {
  format: SlotFormat
}

export interface EmailToolbarProps {
  variables: EmailVariable[]
  activeSlot: ActiveSlot | null
  disabled: boolean
  floatingAnchor?: HTMLElement | null
}

const NORMAL_TEXT: SelectOption = { id: 'paragraph', label: 'Normal text' }
const HEADING: SelectOption = { id: 'h3', label: 'Heading' }
const TEXT_STYLES = [NORMAL_TEXT, HEADING]

const Divider: FC = () => <div className='mx-1 rich-text-toolbar-divider' />

interface ToolButtonProps {
  icon: string
  label: string
  active?: boolean
  disabled: boolean
  onClick: () => void
}

const ToolButton: FC<ToolButtonProps> = ({ icon, label, active = false, disabled, onClick }) => (
  <Button
    variant='borderless'
    className={classNames('p-0 fs-5 d-flex align-items-center justify-content-center rich-text-button', styles.toolButton, {
      'rich-text-button-active': active,
    })}
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
  >
    <i className={classNames('bi', icon)} />
  </Button>
)

export const EmailToolbar: FC<EmailToolbarProps> = ({ variables, activeSlot, disabled, floatingAnchor = null }) => {
  const { editor, formatting, undoRedo } = useRichTextToolbar()
  const [variablesOpen, setVariablesOpen] = useState(false)
  const savedSelection = useRef<BaseSelection | null>(null)
  const formattingDisabled = disabled || activeSlot?.format !== 'rich'
  const insertable = variables.filter((variable) => variable.insertable)
  const textStyle = formatting.selectedBlockType === 'h3' ? HEADING : NORMAL_TEXT
  const insertDisabled = disabled || activeSlot === null

  const setBlocks = (create: () => ReturnType<typeof $createParagraphNode> | ReturnType<typeof $createHeadingNode>) =>
    editor.update(() => {
      const selection = $getSelection()
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, create)
      }
    })

  const toggleList = (listType: 'bullet' | 'number') => {
    if (formatting.selectedBlockType === listType) {
      setBlocks(() => $createParagraphNode())
      return
    }
    editor.dispatchCommand(listType === 'bullet' ? INSERT_UNORDERED_LIST_COMMAND : INSERT_ORDERED_LIST_COMMAND, undefined)
  }

  const toggleVariables = (open: boolean) => {
    if (open) {
      savedSelection.current = editor.getEditorState().read(() => $getSelection()?.clone() ?? null)
    }
    setVariablesOpen(open)
  }

  const insertVariable = (variable: EmailVariable) => {
    setVariablesOpen(false)
    editor.focus()
    editor.update(() => {
      const selection = $isRangeSelection($getSelection()) ? $getSelection() : (savedSelection.current?.clone() ?? null)
      if ($isRangeSelection(selection)) {
        $setSelection(selection)
        selection.insertNodes([$createVariableNode(variable.name, variable.label)])
      }
    })
  }

  return (
    <div
      role='toolbar'
      aria-label='Text formatting'
      className='rich-text-container d-flex flex-wrap align-items-center gap-1 p-3 border rounded-4 zocks-border-color-gray-200 zocks-bg-color-white'
    >
      <div className={styles.textStyle}>
        <SelectDropdown
          className='py-1 px-2'
          options={TEXT_STYLES}
          initialSelectedOption={textStyle}
          onOptionSelected={(option) => setBlocks(() => (option.id === HEADING.id ? $createHeadingNode('h3') : $createParagraphNode()))}
          icon='bi-fonts'
          iconClassName='rich-text-toolbar-icon'
          disabled={formattingDisabled}
        />
      </div>
      <Divider />
      <ToolButton icon='bi-type-bold' label='Bold' active={formatting.isBold} disabled={formattingDisabled} onClick={formatting.formatBold} />
      <ToolButton icon='bi-type-italic' label='Italic' active={formatting.isItalic} disabled={formattingDisabled} onClick={formatting.formatItalic} />
      <ToolButton
        icon='bi-type-underline'
        label='Underline'
        active={formatting.isUnderline}
        disabled={formattingDisabled}
        onClick={formatting.formatUnderline}
      />
      <ToolButton
        icon='bi-type-strikethrough'
        label='Strikethrough'
        active={formatting.isStrikethrough}
        disabled={formattingDisabled}
        onClick={formatting.formatStrikethrough}
      />
      <Divider />
      <ToolButton icon='bi-link' label='Link' active={formatting.isLink} disabled={formattingDisabled} onClick={formatting.insertLink} />
      <Divider />
      <ToolButton
        icon='bi-list-ul'
        label='Bulleted list'
        active={formatting.selectedBlockType === 'bullet'}
        disabled={formattingDisabled}
        onClick={() => toggleList('bullet')}
      />
      <ToolButton
        icon='bi-list-ol'
        label='Numbered list'
        active={formatting.selectedBlockType === 'number'}
        disabled={formattingDisabled}
        onClick={() => toggleList('number')}
      />
      <Divider />
      <Dropdown show={variablesOpen && !insertDisabled} onToggle={toggleVariables} autoClose='outside'>
        <Dropdown.Toggle
          as='button'
          bsPrefix='insert-variable-toggle'
          disabled={insertDisabled}
          className={classNames('d-flex align-items-center gap-2 px-2 rounded-2 border paragraph-small bold zocks-text-primary', styles.insertVariable, {
            'zocks-bg-color-primary-light zocks-border-color-primary': variablesOpen,
            'zocks-bg-color-white zocks-border-color-gray-300': !variablesOpen,
            'opacity-50': insertDisabled,
          })}
        >
          <i className='bi bi-braces' />
          Insert variable
        </Dropdown.Toggle>
        <Dropdown.Menu className='p-0 border-0 bg-transparent'>
          {variablesOpen ? <InsertVariableMenu variables={insertable} onInsert={insertVariable} /> : null}
        </Dropdown.Menu>
      </Dropdown>
      <Divider />
      <ToolButton icon='bi-arrow-counterclockwise' label='Undo' disabled={disabled || !undoRedo.canUndo} onClick={undoRedo.undo} />
      <ToolButton icon='bi-arrow-clockwise' label='Redo' disabled={disabled || !undoRedo.canRedo} onClick={undoRedo.redo} />
      {floatingAnchor ? <RichTextLinkEditor editor={editor} formatting={formatting} anchorElem={floatingAnchor} /> : null}
    </div>
  )
}
